import isNumber from 'lodash/isNumber';
import { useMemo } from 'react'; // just for editor, this is the padding at the top of the canvas

interface Props {
  offsetTop: string;
  offsetLeft: string;
  containerCss: Record<string, unknown>;
}

const getPositionStartMultiplier = (offset: string) => 0.01 * (100 - parseFloat(offset));
const getPositionEndMultiplier = (offset: string) => 0.01 * parseFloat(offset);

const getHorizontalSpacingOffsets = (
  offsetLeft: string,
  marginLeft: number | string,
  marginRight: number | string,
) => {
  const multiplierStart = getPositionStartMultiplier(offsetLeft);
  const multiplierEnd = getPositionEndMultiplier(offsetLeft);

  return {
    start: multiplierStart * (isNumber(marginLeft) ? marginLeft : parseFloat(marginLeft)),
    end: multiplierEnd * (isNumber(marginRight) ? marginRight : parseFloat(marginRight)),
  };
};

const getVerticalSpacingOffsets = (
  offsetTop: string,
  marginTop: number | string,
  marginBottom: number | string,
) => {
  const multiplierStart = getPositionStartMultiplier(offsetTop);
  const multiplierEnd = getPositionEndMultiplier(offsetTop);

  return {
    start: multiplierStart * (isNumber(marginTop) ? marginTop : parseFloat(marginTop)),
    end: multiplierEnd * (isNumber(marginBottom) ? marginBottom : parseFloat(marginBottom)),
  };
};
/**
 * Calculates the modal's top/left positioning based on its offsets and spacing
 *
 * @param {offsetTop, offsetLeft, containerCss}
 * @param {string} offsetTop - percentage string for vertical positioning,
 * @param {string} offsetLeft - percentage string for horizontal positioning,
 * @param {Record<string, unknown>} containerCss - css properties applied to modal from the editor toolbox
 * @returns {Record<string, unknown>} - the calculated top/left values and all other css properties applied to the modal
 */
export const useModalPositioning = ({ offsetTop, offsetLeft, containerCss }: Props) => {
  // get toolbox css values, default margins to 0 if not set by user or are null from css toggle
  const marginLeft = containerCss.marginLeft ?? '0px';
  const marginRight = containerCss.marginRight ?? '0px';
  const marginTop = containerCss.marginTop ?? '0px';
  const marginBottom = containerCss.marginBottom ?? '0px';

  const horizontalOffsets = useMemo(
    () => getHorizontalSpacingOffsets(offsetLeft, marginLeft as any, marginRight as any),
    [offsetLeft, marginLeft, marginRight],
  );

  const left = `calc(${offsetLeft} + ${horizontalOffsets.start}px - ${horizontalOffsets.end}px)`;

  const verticalOffsets = useMemo(
    () => getVerticalSpacingOffsets(offsetTop, marginTop as any, marginBottom as any),
    [offsetTop, marginTop, marginBottom],
  );

  const top = `calc(${offsetTop} + ${verticalOffsets.start}px - ${verticalOffsets.end}px)`;

  const translateY = `calc(-${offsetTop})`;
  const translateX = `calc(-${offsetLeft})`;

  return {
    ...containerCss,
    top,
    left,
    transform: `translate(${translateX}, ${translateY})`,
    marginLeft,
    marginRight,
    marginTop,
    marginBottom,
  } as Record<string, string | number>;
};
